/**
 * DSL_Socials
 * Social media QR codes component
 */

import { useMemo, memo, useRef, useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { FaLinkedin, FaInstagram, FaYoutube } from 'react-icons/fa'
import { trackEvent } from '../../utils/analytics'
import { getMediaQueryKey } from '../../utils/deviceDetection'

const HUD_COLOR = '#EE3A17'

// Artemis mark for the kiosk-only QR — same { size, color } signature as the
// react-icons components so it drops into the .dsl-qr-icon slot unchanged.
// artemis-filled.svg is pre-filled with the HUD red, so color is unused.
const ArtemisIcon = memo(function ArtemisIcon({ size }) {
  return (
    <img
      src="/images/artemis-filled.svg"
      alt="Artemis"
      style={{ width: size, height: 'auto' }}
    />
  )
})

// Memoized QR code component to prevent regeneration
const MemoizedQRCode = memo(function MemoizedQRCode({ url, size }) {
  return (
    <QRCodeSVG
      value={url}
      size={size}
      level="H"
      includeMargin={false}
      fgColor="#EE3A17"
      bgColor="transparent"
    />
  )
})

export default function DSL_Socials({ isTransitioning }) {
  // Two large-display tiers: 2560px+ (the original "4K-ish" bump) and true 4K
  // at 3840px+, where the QR codes / icons step up another ~50% (the CSS
  // .dsl-qr-wrapper / .dsl-window sizing in the 3840px block matches this).
  const [isLarge, setIsLarge] = useState(() => window.matchMedia('(min-width: 2560px)').matches)
  const [is4K, setIs4K] = useState(() => window.matchMedia('(min-width: 3840px)').matches)
  const [isTablet, setIsTablet] = useState(() => window.matchMedia('(min-width: 900px) and (max-width: 1919px) and (orientation: landscape)').matches)
  useEffect(() => {
    const mqLarge = window.matchMedia('(min-width: 2560px)')
    const mq4K = window.matchMedia('(min-width: 3840px)')
    const mqTablet = window.matchMedia('(min-width: 900px) and (max-width: 1919px) and (orientation: landscape)')
    const onLarge = (e) => setIsLarge(e.matches)
    const on4K = (e) => setIs4K(e.matches)
    const onTablet = (e) => setIsTablet(e.matches)
    mqLarge.addEventListener('change', onLarge)
    mq4K.addEventListener('change', on4K)
    mqTablet.addEventListener('change', onTablet)
    return () => {
      mqLarge.removeEventListener('change', onLarge)
      mq4K.removeEventListener('change', on4K)
      mqTablet.removeEventListener('change', onTablet)
    }
  }, [])

  // QR / icon sizes follow the scaling rule off the 4K reference (4K = gospel,
  // 1440 ≈ ⅔, 1080 = ½, Tablet Landscape = ¼). The matching .dsl-qr-wrapper box
  // sizes live in the per-tier style.css blocks.
  // Tablet QR/icon bumped +50% (per user) to scale the whole JOIN US screen —
  // must match the .dsl-qr-wrapper / .dsl-qr-icon tablet CSS (146.25 / 28.875).
  const qrSize = is4K ? 390 : (isLarge ? 260 : isTablet ? 146.25 : 195)
  const iconSize = is4K ? 77 : (isLarge ? 51 : isTablet ? 28.875 : 38)

  // Kiosk mode (?kiosk=true) is the unattended Festival-of-Speed display — the
  // social buttons must NOT navigate the kiosk browser away to an external page
  // (visitors scan the QR instead). Everywhere else, tapping/clicking a social
  // opens its page. Fixed for the page's lifetime, so the socials list length
  // (and the per-item hooks in the map below) stays stable across renders.
  const isKioskMode = new URLSearchParams(window.location.search).get('kiosk') === 'true'

  // Memoize socials array to prevent recreation
  const socials = useMemo(() => {
    const list = [
      {
        name: 'LINKEDIN',
        url: 'https://www.linkedin.com/company/darkstarlabs/',
        Icon: FaLinkedin
      },
      {
        name: 'INSTAGRAM',
        url: 'https://www.instagram.com/darkstarlabs.io/',
        Icon: FaInstagram
      },
      {
        name: 'YOUTUBE',
        url: 'https://www.youtube.com/@DarkStarLabs',
        Icon: FaYoutube
      }
    ]
    // Kiosk-only: QR to the Artemis experience site
    if (isKioskMode) {
      list.push({
        name: 'THIS APP',
        url: 'https://artemis.darkstarlabs.io',
        Icon: ArtemisIcon
      })
    }
    return list
  }, [isKioskMode])
  
  // Memoize empty style object
  const emptyStyle = useMemo(() => ({}), [])
  
  // Memoize cursor style to prevent object creation
  const cursorPointerStyle = useMemo(() => ({ cursor: 'pointer' }), [])

  const isMobilePortrait = getMediaQueryKey() === 'mobilePortrait'

  const openSocial = (social) => {
    if (isKioskMode) return
    trackEvent(`${social.name.toLowerCase()}_click`)
    window.open(social.url, '_blank')
  }

  return (
    <div className="dsl-socials-container">
      {socials.map((social, index) => {
        const IconComponent = social.Icon
        const flickerDelay = index * 0.15
        
        // Memoize flicker style object per index to prevent recreation
        const flickerStyle = useMemo(() => ({
          '--flicker-delay-1': `${flickerDelay}s`,
          '--flicker-delay-2': `${flickerDelay + 0.05}s`,
          '--flicker-delay-3': `${flickerDelay + 0.1}s`
        }), [flickerDelay])
        
        return (
          <div 
            key={social.name}
            className={`dsl-social-item ${isTransitioning ? 'flickering' : ''}`}
            style={isTransitioning ? flickerStyle : emptyStyle}
          >
            <h3 className="dsl-social-label">{social.name}</h3>
            {isMobilePortrait ? (
              /* Mobile portrait: just the icon, no QR */
              <div
                onClick={() => openSocial(social)}
                style={{
                  cursor: isKioskMode ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px'
                }}
              >
                <IconComponent size={40} color="#EE3A17" />
              </div>
            ) : (
              /* Desktop/tablet: full QR wrapper. Clickable to open the social page
                 (restored) — except in kiosk mode, where only the QR is offered. */
              <div
                className="dsl-qr-wrapper"
                onClick={isKioskMode ? undefined : () => openSocial(social)}
                style={isKioskMode ? emptyStyle : cursorPointerStyle}
              >
                <div className="dsl-qr-background" />
                <QRFrame size={qrSize} />
                <div className="dsl-qr-code">
                  <MemoizedQRCode url={social.url} size={qrSize} />
                </div>
                <div className="dsl-qr-icon">
                  <IconComponent size={iconSize} color="#EE3A17" />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// QRFrame accepts a size prop so it can scale at 4K
const QRFrame = memo(function QRFrame({ size = 200 }) {
  const cornerSize = 20
  
  return (
    <svg className="dsl-qr-frame" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Top left corner */}
      <g>
        <line x1="0" y1="0" x2="0" y2={cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1="0" y1="0" x2={cornerSize} y2="0" stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Top right corner */}
      <g>
        <line x1={size} y1="0" x2={size} y2={cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1={size} y1="0" x2={size - cornerSize} y2="0" stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Bottom left corner */}
      <g>
        <line x1="0" y1={size} x2="0" y2={size - cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1="0" y1={size} x2={cornerSize} y2={size} stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Bottom right corner */}
      <g>
        <line x1={size} y1={size} x2={size} y2={size - cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1={size} y1={size} x2={size - cornerSize} y2={size} stroke={HUD_COLOR} strokeWidth="2" />
      </g>
    </svg>
  )
})
